import React, { useState } from "react";

import { skipToken, useMutation, useQuery } from "@tanstack/react-query";

import { addRecalculationTrigger, getLatestRecalculationTrigger, getRecalculationTrigger } from "services";

import { incomplete } from "utils";

import { ADD_VERIFICATION_RECALCULATION_TRIGGER, GET_LATEST_VERIFICATION_RECALCULATION_TRIGGER, GET_VERIFICATION_RECALCULATION_TRIGGER } from "consts";

import { AdminSection, Alert, Button, Col, Form, Input, Modal, Row, TextArea, Message as message } from "components";

export const VerificationRecalculation = () => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingValues, setPendingValues] = useState<{ title: string; description: string } | null>(null);

  const handleFinish = (values: { title: string; description: string }) => {
    setPendingValues(values);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (pendingValues) {
      mutate(pendingValues);
      setShowConfirmModal(false);
      setPendingValues(null);
    }
  };

  const {
    mutate,
    data: mutationData,
    isPending,
    isSuccess,
  } = useMutation({
    mutationKey: [ADD_VERIFICATION_RECALCULATION_TRIGGER],
    mutationFn: addRecalculationTrigger,
    onSuccess: () => {
      message({ content: "Recalculation triggered successfully", icon: "gremlin" });
      // Refetch latest trigger after successful mutation
      refetchLatestTrigger();
    },
  });

  // Fetch the latest trigger on page load
  const { data: latestTrigger, refetch: refetchLatestTrigger } = useQuery({
    queryKey: [GET_LATEST_VERIFICATION_RECALCULATION_TRIGGER],
    queryFn: getLatestRecalculationTrigger,
    refetchInterval: 10000, // 10 seconds
  });

  // Fetch specific trigger if mutation was successful
  const { data } = useQuery({
    queryKey: [GET_VERIFICATION_RECALCULATION_TRIGGER, mutationData?.id],
    queryFn: mutationData?.id ? () => getRecalculationTrigger({ id: mutationData.id }) : skipToken,
    refetchInterval: 10000, // 10 seconds
  });

  // Determine if recalculation is currently running
  const isRecalculationRunning = Boolean(latestTrigger && "id" in latestTrigger && !latestTrigger.processed);

  return (
    <>
      <AdminSection
        headline="Trigger verification points recalculation"
        description="This page gives you the option to recalculate payouts and points/credits of fields that are currently in verification. Please provide a title and description as a reason as to why the recalculation is necessary."
      />

      {isRecalculationRunning && (
        <Row style={{ marginBottom: 24 }}>
          <Col>
            <Alert
              className={undefined}
              closable={false}
              type="warning"
              headline="Recalculation in Progress"
              description={
                <>
                  A verification points recalculation is currently running. Please wait for it to complete before triggering a new recalculation.
                  {latestTrigger && (
                    <>
                      <br />
                      <strong>Latest trigger:</strong> {latestTrigger.title} (ID: {latestTrigger.id})
                    </>
                  )}
                </>
              }
            />
          </Col>
        </Row>
      )}

      {isSuccess && (
        <Row>
          <Col>
            <p>
              Recalculation was triggered you can safely navigate away. You can stay on the page to check if the recalculation is done. If you want to check
              later, you can go to metabase and see if verifications_recalculation_trigger with id {mutationData?.id} is processed.
            </p>

            <p>Status: {data?.processed ? "All points were recalculated" : "Recalculation still ongoing"}</p>
          </Col>
        </Row>
      )}

      <Form name="verification-points-form" onFinish={handleFinish} onFinishFailed={incomplete}>
        <Row>
          <Col md={12}>
            <Input name="title" label="Title" rules={[{ required: true, message: "Title is required" }]} disabled={isRecalculationRunning} />
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <TextArea
              name="description"
              label="Description"
              rules={[{ required: true, message: "Description is required" }]}
              autoSize={{ minRows: 3 }}
              disabled={isRecalculationRunning}
            />
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Button type="primary" htmlType="submit" loading={isPending} disabled={isRecalculationRunning || isPending}>
              Save
            </Button>
          </Col>
        </Row>
      </Form>

      <Modal
        showModal={showConfirmModal}
        onOk={handleConfirm}
        onCancel={() => {
          setShowConfirmModal(false);
          setPendingValues(null);
        }}
        okText="Confirm"
        cancelText="Cancel"
        title="Confirm Verification Points Recalculation"
        content={
          <>
            <p>You are about to trigger a recalculation of verification points and payouts for all fields currently in verification.</p>
            <p>
              <strong>This action will:</strong>
            </p>
            <ul style={{ marginLeft: 20, marginTop: 8, marginBottom: 8 }}>
              <li>Recalculate verification points and credits for all fields in verification</li>
              <li>Update payout calculations based on the new points</li>
              <li>Create a new recalculation trigger record with the following details:</li>
            </ul>
            {pendingValues && (
              <div style={{ marginTop: 12, padding: 12, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
                <p style={{ margin: 0 }}>
                  <strong>Title:</strong> {pendingValues.title}
                </p>
                <p style={{ margin: "8px 0 0 0" }}>
                  <strong>Description:</strong> {pendingValues.description}
                </p>
              </div>
            )}
            <p style={{ marginTop: 16, fontWeight: "bold" }}>This process may take some time to complete. You can monitor the progress on this page.</p>
          </>
        }
      />
    </>
  );
};
